/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import styles from './styles.module.scss';
import { useGetAssessmentInfo, useAssessmentSections, getCandidateAreasCovered } from 'hooks';
import { JobDescription } from 'components/job-description';
import { Spinner } from 'components/loader';
import { MessageDisplay } from 'components/message-display';
import { Message } from '../chat-messages';
import { PrecisionChatBotIcon, TickIcon } from 'assets';
import { RecruitmentOptions } from '../recruitment-cards';
import { ConversationOption } from 'features';
import { AssessmentInformation } from 'components/assessment-information';
import { SampleQuestionsComponent } from 'components/sample-questions';
import { ProfileFormComponent } from 'components/profile-info-form';
import { AddToCalendar } from 'components/add-to-calendar';
import { UploadedJobDescription } from 'components/uploaded-job-description';
import { format, parseISO } from 'date-fns';
import { Routes } from 'router';
import { toTitleCase } from 'utils/titleCase';

const parsePythonDict = (str: string): any => {
  if (typeof str !== 'string') return str;
  try { return JSON.parse(str); } catch {}
  // Python repr uses single quotes as delimiters, but double quotes for values containing apostrophes.
  // Walk through char-by-char to only convert single-quote delimiters to double quotes.
  let result = '';
  let inDouble = false;
  let inSingle = false;
  for (let i = 0; i < str.length; i++) {
    const ch = str[i];
    const prev = i > 0 ? str[i - 1] : '';
    if (ch === '"' && !inSingle && prev !== '\\') {
      inDouble = !inDouble;
      result += ch;
    } else if (ch === "'" && !inDouble && prev !== '\\') {
      inSingle = !inSingle;
      result += '"';
    } else {
      result += ch;
    }
  }
  const fixed = result
    .replace(/\bNone\b/g, 'null')
    .replace(/\bTrue\b/g, 'true')
    .replace(/\bFalse\b/g, 'false');
  return JSON.parse(fixed);
};

const toBool = (value: any): boolean =>
  value === true || value === 'true' || value === 1 || value === '1';

// Whether the employer's offered salary range should be shown to the candidate
// in the job description. Controlled solely by `display_salary` and only when a
// valid range exists. Kept independent from whether the candidate is asked for
// their own salary expectation (see `getShowSalaryInForm`).
const shouldDisplayEmployerSalary = (basicExpectation: any): boolean =>
  !!basicExpectation
  && toBool(basicExpectation.display_salary)
  && !!basicExpectation.min_salary_range_amount
  && !!basicExpectation.max_salary_range_amount;

export const initialOptions = (): ConversationOption[] => [
  {
    text: "Okay",
    value: "okay",
    Icon: TickIcon
  },
  {
    text: "Not Fit",
    value: "not_fit"
  }
];

const CandidateChatMessages = ({ assessmentId }: { assessmentId: string }) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const { fetchAssessmentInfo, assessmentInfo, isLoading: isLoadingGetAssessmentInfo, error: assessmentInfoError } = useGetAssessmentInfo(assessmentId);
  const [currentOptions, setCurrentOptions] = useState<ConversationOption[] | null>(initialOptions());
  const [showOption, setShowOption] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showNotFitModal, setShowNotFitModal] = useState<boolean>(false);

  // NOTE: We intentionally do NOT fetch /candidate/default-questions/ here.
  // Doing so shipped the entire question bank + scoring rubric/answer keys to
  // the candidate's browser on the pre-assessment preview, before they ever
  // started. The "areas covered" list comes from /assessment/sections/ instead,
  // which only reports which metrics the recruiter kept. Until it resolves,
  // AssessmentInformation falls back to its default area list.
  const { sections: enabledSections } = useAssessmentSections(assessmentId, { anonymous: true });
  const areasCovered = getCandidateAreasCovered(enabledSections);

  // Whether the candidate's application form asks for their salary expectation.
  // Defaults to NOT asking when basic expectations (or the salary requirement)
  // have been removed, so disabling/deleting the requirement reliably removes
  // the prompt. The employer's salary *display* is controlled separately by
  // `display_salary`.
  const getShowSalaryInForm = (basicExpectation: any) => {
    if (!basicExpectation) return false;
    if (typeof basicExpectation.show_salary_in_form === 'boolean') return basicExpectation.show_salary_in_form;
    if (typeof basicExpectation.require_salary_in_form === 'boolean') return basicExpectation.require_salary_in_form;
    if (typeof basicExpectation.hide_salary_in_form === 'boolean') return !basicExpectation.hide_salary_in_form;
    return basicExpectation.require_salary_range ?? false;
  };


  const stage2Options = (): ConversationOption[] => [
    {
      text: "View Guide",
      value: "yes",
      Icon: TickIcon
    },
    {
      text: "Not interested",
      value: "not_interested"
    }
  ];

  const stage3Options = (): ConversationOption[] => [
    {
      text: "Okay, let's do that",
      value: "do_that",
      Icon: TickIcon
    },
    // {
    //   text: "Not now, I will come back later",
    //   value: "not_now"
    // }
  ];
  const stage3_1Options = (): ConversationOption[] => [
    {
      text: "Resume Asseessment",
      value: "do_that",
      Icon: TickIcon
    },
    {
      text: "Add to Google Calendar",
      value: "calendar"
    }
  ];

  const stage4Options = (): ConversationOption[] => [
    {
      text: "StartAssessment",
      value: "start",
      Icon: TickIcon
    },
  ];


  useEffect(() => {
    if (assessmentId) {
      fetchAssessmentInfo();
    }
  }, [assessmentId]);

  useEffect(() => {
    if (assessmentInfoError) {
      setMessages([{
        id: 'error-1',
        content: 'Failed to load assessment details. Please try again.',
        sender: 'system',
        timestamp: new Date(),
      }]);
      setShowOption(false);
      return;
    }
  }, [assessmentInfoError]);

  useEffect(() => {
    if (assessmentInfo && messages.length === 0) {
      const welcomeMessage: Message = {
        id: 'welcome-1',
        content: 'Welcome! Please review the job description below.',
        sender: 'system',
        timestamp: new Date(),
        isProcessed: true,
      };

      const jobDescriptionMessage: Message = {
        id: 'jd-1',
        content: '',
        sender: 'system',
        timestamp: new Date(),
        componentType: 'jobDescription',
        componentData: {
          jdPreview: {
            jobTitle: assessmentInfo.data.title || 'Untitled Job',
            organization: assessmentInfo?.data?.company_info?.name || 'Organization Not Available',
            employmentType: assessmentInfo.data.basic_expectation?.employment_type || 'Not specified',
            work_mode: assessmentInfo.data.basic_expectation?.work_mode || '',
            salaryRange:
              shouldDisplayEmployerSalary(assessmentInfo.data.basic_expectation)
                ? `${assessmentInfo.data.basic_expectation!.salary_range_currency || ''} ${Number(assessmentInfo.data.basic_expectation!.min_salary_range_amount).toLocaleString()} - ${Number(assessmentInfo.data.basic_expectation!.max_salary_range_amount).toLocaleString()} ${assessmentInfo.data.basic_expectation!.salary_range_term || ''}`.trim()
                : 'Not specified',
            experience_level: assessmentInfo.data.basic_expectation?.experience_level || 'Not specified',
            experience_environment: assessmentInfo.data.basic_expectation?.experience_environment || 'Not specified',
            salary_range_term: assessmentInfo.data.basic_expectation?.salary_range_term || 'Not specified',
            salary_range_currency: assessmentInfo.data.basic_expectation?.salary_range_currency || 'Not specified',
            years_of_experience: assessmentInfo.data.basic_expectation?.years_of_experience || 'Not specified',
            about: assessmentInfo.data.about || 'Not specified',
            responsibilities: (() => {
              const coreResp = assessmentInfo.data.employer_core_responsibility;
              // When the employer has a core-responsibility record, treat it as the
              // source of truth so candidate-side edits AND deletions are reflected.
              // We only fall back to the original job_description JSON for legacy
              // assessments that never had a separate record — otherwise a deleted
              // (cleared) section would keep showing the stale original responsibilities.
              if (coreResp) {
                return [
                  { title: coreResp.responsibility1, description: coreResp.description1 },
                  { title: coreResp.responsibility2, description: coreResp.description2 },
                  { title: coreResp.responsibility3, description: coreResp.description3 },
                  { title: coreResp.responsibility4, description: coreResp.description4 },
                  { title: coreResp.responsibility5, description: coreResp.description5 },
                ].filter(r => r.title && r.description);
              }
              try {
                const jdStr = assessmentInfo.data.job_description;
                if (!jdStr) return [];
                const parsed = parsePythonDict(jdStr);
                return Array.isArray(parsed?.responsibilities) ? parsed.responsibilities : [];
              } catch {
                return [];
              }
            })(),
            qualifications: (() => {
              try {
                const jdStr = assessmentInfo.data.job_description;
                if (!jdStr) return [];
                const parsed = parsePythonDict(jdStr);
                if (!Array.isArray(parsed?.qualifications)) return [];
                return parsed.qualifications
                  .map((q: any) => ({ description: q?.description || '' }))
                  .filter((q: { description: string }) => q.description);
              } catch {
                return [];
              }
            })(),
            location: assessmentInfo.data.basic_expectation?.address || undefined,
            whatWeOffer: (() => {
              try {
                const jdStr = assessmentInfo.data.job_description;
                if (!jdStr) return undefined;
                const parsed = parsePythonDict(jdStr);
                const raw = parsed?.what_we_offer || parsed?.whatWeOffer || parsed?.benefits;
                if (Array.isArray(raw)) return raw.map((i: any) => i?.description || i?.text || String(i));
                return undefined;
              } catch { return undefined; }
            })(),
          },
        },
      };
      const uploadedJobDescriptionMessage: Message = {
        id: 'jd-1',
        content: '',
        sender: 'system',
        timestamp: new Date(),
        componentType: 'jobDescriptionUpload',
        componentData: {
          jdPreview: {
            jobTitle: assessmentInfo.data.title || 'Untitled Job',
            organization: assessmentInfo?.data?.company_info?.name || 'Organization Not Available',
            employmentType: assessmentInfo?.data?.basic_expectation?.employment_type || 'Not specified',
            work_mode: assessmentInfo?.data?.basic_expectation?.work_mode || '',
            salaryRange:
              shouldDisplayEmployerSalary(assessmentInfo?.data?.basic_expectation)
                ? `${assessmentInfo!.data.basic_expectation!.salary_range_currency || ''} ${Number(assessmentInfo!.data.basic_expectation!.min_salary_range_amount).toLocaleString()} - ${Number(assessmentInfo!.data.basic_expectation!.max_salary_range_amount).toLocaleString()} ${assessmentInfo!.data.basic_expectation!.salary_range_term || ''}`.trim()
                : 'Not specified',
            salary_range_term: assessmentInfo?.data?.basic_expectation?.salary_range_term || 'Not specified',
            salary_range_currency: assessmentInfo?.data?.basic_expectation?.salary_range_currency || 'Not specified',
            location: assessmentInfo?.data?.basic_expectation?.address || undefined,
            uploadedJD: assessmentInfo?.data?.uploaded_jd
          }
        }
      }
      if (assessmentInfo?.data?.uploaded_jd) {
        setMessages([welcomeMessage, uploadedJobDescriptionMessage]);
      } else {
        setMessages([welcomeMessage, jobDescriptionMessage]);
      }
    }
  }, [assessmentInfo]);

  const handleOptionSelect = (option: string) => {
    const userMessage: Message = {
      id: `${Date.now()}-user-${Math.random().toString(36).substr(2, 9)}`,
      content: currentOptions?.find(opt => opt.value === option)?.text || option,
      sender: 'user',
      timestamp: new Date(),
    };
    setMessages([...messages, userMessage]);

    if (option === 'okay') {
      const assessmenntInfoMessage: Message = {
        id: `${Date.now()}-assessment-information`,
        content: '',
        sender: 'system',
        timestamp: new Date(),
        componentType: 'assessmentInformation',
        componentData: null,
        state: 'assessment_information',
        isProcessed: true,
      };
      setMessages([...messages, userMessage, assessmenntInfoMessage]);
      setShowOption(true);
      setCurrentOptions(stage2Options);
      setIsLoading(false);
    } else if (option === 'yes') {
      const sampleQuestionMessage: Message = {
        id: `${Date.now()}-sample-question`,
        content: '',
        sender: 'system',
        timestamp: new Date(),
        componentType: 'sampleQuestion',
        componentData: null,
        state: 'sample_question',
        isProcessed: true,
      };
      setMessages([...messages, userMessage, sampleQuestionMessage]);
      setShowOption(true);
      setCurrentOptions(stage3Options);
      setIsLoading(false);
    } else if (option === 'do_that' || option === 'not_interested') {
      const profileFormMessage: Message = {
        id: `${Date.now()}-profile-form`,
        content: '',
        sender: 'system',
        timestamp: new Date(),
        componentType: 'profileForm',
        componentData: null,
        state: 'profile_form',
        isProcessed: true,
      };
      setMessages([...messages, userMessage, profileFormMessage]);
      setShowOption(false);
      setCurrentOptions(stage4Options);
      setIsLoading(false);
    } else if (option === 'not_now') {
      const profileFormMessage: Message = {
        id: `${Date.now()}-come-back`,
        content: '🤔 \n This means you aren’t moving forward with the assessment at the moment. You can come back anytime within the assessment timeframe and complete the assessment.\n See you soon. \n You can resume the assessment by clicking the button below',
        sender: 'system',
        timestamp: new Date(),
        componentData: null,
        state: 'come_back',
        isProcessed: true,
      };
      setMessages([...messages, userMessage, profileFormMessage]);
      setShowOption(true);
      setCurrentOptions(stage3_1Options);
      setIsLoading(false);
    } else if (option === 'not_fit') {
      setShowNotFitModal(true);
      setShowOption(false);
      setIsLoading(false);
      return;
    } else if (option === 'calendar') {
      const profileFormMessage: Message = {
        id: `${Date.now()}-calendar`,
        content: '',
        sender: 'system',
        timestamp: new Date(),
        componentType: 'calendar',
        componentData: null,
        state: 'calendar',
        isProcessed: true,
      };
      setMessages([...messages, userMessage, profileFormMessage]);
      setShowOption(false);
      setIsLoading(false);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (currentOptions?.[0].value !== 'okay')
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  if (isLoadingGetAssessmentInfo) {
    return <div className={styles.loading}><Spinner /></div>;
  }

  const startDateRaw = assessmentInfo?.data?.start_date;
  const startDate = startDateRaw ? parseISO(startDateRaw) : null;
  const isClosed = assessmentInfo?.data?.status === 'closed';
  const isScheduled = !isClosed && startDate ? startDate.getTime() > Date.now() : false;
  const showStatusPill = isScheduled || isClosed;

  const statusPill = !showStatusPill ? null : isClosed ? (
    <span className="mt-5 inline-flex items-center px-3 py-1 rounded-full border border-[#E62E2E] text-[#E62E2E] bg-[#FFE6E6] text-sm font-medium w-fit">
      No Active Assessment
    </span>
  ) : (
    <span className="mt-5 inline-flex items-center px-3 py-1 rounded-full border border-[#DB8400] bg-[#FFF5E6] text-[#DB8400] text-sm font-medium w-fit">
      Upcoming • {startDate ? format(startDate, 'dd MMM yyyy') : ''}
    </span>
  );

  return (
    <>
      {showNotFitModal && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-8 max-w-[420px] w-[90%] text-center">
            <h3 className="text-xl font-semibold text-[#012169] mb-3">
              Are you sure?
            </h3>
            <p className="text-sm text-[#475467] mb-6">
              You are about to leave this assessment. You can always come back within the assessment timeframe to complete it.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => {
                  setShowNotFitModal(false);
                  setShowOption(true);
                }}
                className="px-6 py-2.5 rounded-md text-sm font-medium border border-[#012169] text-[#012169] bg-transparent hover:bg-[#f0f0ff] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  window.location.href = Routes.home;
                }}
                className="px-6 py-2.5 rounded-md text-sm font-medium bg-[#012169] text-white border border-[#012169] hover:bg-[#332fa0] transition-colors"
              >
                Yes, leave
              </button>
            </div>
          </div>
        </div>
      )}
      <div className={styles.container}>
        <div className={styles.chatContainer}>
          {messages.map((message, index) => (
            <div key={message.id} className={styles.messageContainer}>
              {!message.componentType && (
                <MessageDisplay key={message.id} message={message} isCandidate />
              )}
              {message.componentType === 'jobDescription' &&
                <div className={styles.jobDescSection}>
                  <div className={styles.iconContainer}>
                    <PrecisionChatBotIcon className={styles.icon} />
                  </div>
                  <div className={`lg:ml-[40px] ml-[20px] ${styles.jd}`}>
                    <JobDescription jdData={message.componentData.jdPreview} assessmentId={assessmentId} hideHeader />
                    {statusPill}
                  </div>
                </div>
              }
              {message.componentType === 'jobDescriptionUpload' &&
                <div className={styles.jobDescSection}>
                  <div className={styles.iconContainer}>
                    <PrecisionChatBotIcon className={styles.icon} />
                  </div>
                  <div className={`${styles.jd}`}>
                    <UploadedJobDescription jdData={message.componentData.jdPreview} pdfFile={message.componentData.jdPreview.uploadedJD} />
                    {statusPill}
                  </div>
                </div>
              }
              {message.componentType === 'assessmentInformation' && message.state === 'assessment_information' && (
                <div className={styles.jobDescSection}>
                  <div className={styles.iconContainer}>
                    <PrecisionChatBotIcon className={styles.icon} />
                  </div>
                  <div className={styles.assessment}>
                    <AssessmentInformation
                      companyName={assessmentInfo?.data?.company_info?.name}
                      // Only the metrics the recruiter kept, from
                      // /assessment/sections/ (falls back to the default list
                      // while the request is in flight).
                      areasCovered={areasCovered}
                      assessmentTitle={assessmentInfo?.data.title || '--/--'}
                      startDate={
                        assessmentInfo?.data.start_date
                          ? format(parseISO(assessmentInfo.data.start_date), "dd MMMM, yyyy")
                          : '--/--'
                      }
                      endDate={
                        assessmentInfo?.data.end_date
                          ? format(parseISO(assessmentInfo.data.end_date), "dd MMMM, yyyy")
                          : '--/--'
                      }
                      totalQuestions={assessmentInfo?.data?.total_questions || 90}
                      {...(() => {
                        const raw = assessmentInfo?.data?.instruction;
                        if (!raw) return {};
                        const lines = raw.split('\n').map((l: string) => l.trim()).filter(Boolean);
                        const reminderLine = lines.find((l: string) => l.toLowerCase().startsWith('remember'));
                        const instructionLines = lines
                          .filter((l: string) => !l.toLowerCase().startsWith('remember'))
                          .map((l: string) => l.replace(/^\d+\.\s*/, ''));
                        return {
                          instructions: instructionLines.length > 0 ? instructionLines : undefined,
                          reminderNote: reminderLine || undefined,
                        };
                      })()}
                      totalTime={
                        assessmentInfo?.data?.duration_in_minutes
                          ? `${assessmentInfo.data.duration_in_minutes} minutes`
                          : undefined
                      }
                    />
                  </div>
                </div>
              )}
              {message.componentType === 'sampleQuestion' && message.state === 'sample_question' && (
                <div className={styles.jobDescSection}>
                  <div className={styles.iconContainer}>
                    <PrecisionChatBotIcon className={styles.icon} />
                  </div>
                  <div className={styles.assessment}>
                    <SampleQuestionsComponent />
                  </div>
                </div>
              )}
              {message.componentType === 'profileForm' && message.state === 'profile_form' && (
                <div className={styles.jobDescSection}>
                  <div className={styles.iconContainer}>
                    <PrecisionChatBotIcon className={styles.icon} />
                  </div>
                  <div className={styles.assessment}>
                    <ProfileFormComponent
                      assessmentId={assessmentId}
                      roleTitle={assessmentInfo?.data?.title}
                      salaryCurrency={assessmentInfo?.data?.basic_expectation?.salary_range_currency}
                      salaryTerm={assessmentInfo?.data?.basic_expectation?.salary_range_term}
                      minSalary={assessmentInfo?.data?.basic_expectation?.min_salary_range_amount}
                      maxSalary={assessmentInfo?.data?.basic_expectation?.max_salary_range_amount}
                      requireCvUpload={assessmentInfo?.data?.basic_expectation?.require_cv_upload}
                      requirePortfolioUpload={assessmentInfo?.data?.basic_expectation?.require_portfolio_upload ?? true}
                      requireEmploymentType={assessmentInfo?.data?.basic_expectation?.require_employment_type ?? true}
                      requireWorkMode={assessmentInfo?.data?.basic_expectation?.require_work_mode ?? true}
                      requireExperienceLevel={assessmentInfo?.data?.basic_expectation?.require_experience_level ?? true}
                      requireYearsOfExperience={assessmentInfo?.data?.basic_expectation?.require_years_of_experience ?? true}
                      requireWorkEnvironment={assessmentInfo?.data?.basic_expectation?.require_work_environment ?? true}
                      requireSalaryRange={getShowSalaryInForm(assessmentInfo?.data?.basic_expectation)}
                    />
                  </div>
                </div>
              )}
              {message.componentType === 'calendar' && message.state === 'calendar' && (
                <div className={styles.jobDescSection}>
                  <div className={styles.iconContainer}>
                    <PrecisionChatBotIcon className={styles.icon} />
                  </div>
                  <div className={styles.calendarSection}>
                    <AddToCalendar />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          ))}
        </div>


        {!showStatusPill && showOption && currentOptions && currentOptions.length > 0 && !['submit_open_ended', 'publish_assessment'].includes(currentOptions[0].value) && (
          <div className={`md:pb-10 ${styles.recruitmentOptions}`}>
            <RecruitmentOptions
              onOptionSelect={handleOptionSelect}
              options={currentOptions}
              containerClass={styles.recruitmentOptionsContainer}
            />
          </div>
        )}
      </div>
    </>

  );
};

export { CandidateChatMessages };
